"use client";

import { c as _c } from "react/compiler-runtime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import VotingModalButton from "@/components/voting/VotingModalButton";
import ApprovalStatusBadge from "@/components/waves/approval/ApprovalStatusBadge";
import DropCurationButton from "@/components/waves/drops/DropCurationButton";
import WinnerDropBadge from "@/components/waves/drops/winner/WinnerDropBadge";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { isOfficiallyApprovedDrop } from "@/helpers/waves/approve-wave.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import Link from "next/link";
import React from "react";
import WaveLeaderboardGalleryItemVotes from "../gallery/WaveLeaderboardGalleryItemVotes";
import { WaveLeaderboardIdentity } from "../identity/WaveLeaderboardIdentity";
interface WaveLeaderboardGridItemCompactFooterProps {
  readonly drop: ExtendedDrop;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly isVotingClosed: boolean;
  readonly canShowVotingAction: boolean;
  readonly onVoteButtonClick: () => void;
}
const getVoteStyle = (userVote: number): string => userVote <= 0 ? "tw-text-iron-400" : "tw-text-iron-300";
function GridItemRankBadge(t0) {
  const $ = _c(6);
  const {
    drop,
    winningThreshold
  } = t0;
  const isApproveDrop = typeof winningThreshold === "number" && winningThreshold > 0;
  if (isApproveDrop) {
    let t1;
    if ($[0] !== drop) {
      t1 = isOfficiallyApprovedDrop(drop) ? <ApprovalStatusBadge approvedAt={drop.winning_context?.decision_time ?? null} /> : null;
      $[0] = drop;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (drop.rank === null) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-flex tw-h-6 tw-min-w-6 tw-items-center tw-justify-center tw-rounded-xl tw-bg-iron-800 tw-px-2 tw-text-xs tw-font-semibold tw-text-iron-400">-</div>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  const t1 = drop.winning_context?.decision_time ?? null;
  let t2;
  if ($[3] !== drop.rank || $[4] !== t1) {
    t2 = <WinnerDropBadge rank={drop.rank} decisionTime={t1} />;
    $[3] = drop.rank;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export const WaveLeaderboardGridItemCompactFooter: React.FC<WaveLeaderboardGridItemCompactFooterProps> = t0 => {
  const $ = _c(59);
  const {
    drop,
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed,
    canShowVotingAction,
    onVoteButtonClick
  } = t0;
  const author = drop.author;
  const authorHandle = author.handle ?? null;
  const hasUserVoted = drop.context_profile_context?.rating !== undefined;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const isNegativeVote = userVote < 0;
  let t1;
  if ($[0] !== userVote) {
    t1 = getVoteStyle(userVote);
    $[0] = userVote;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const voteStyle = t1;
  const votingCreditType = drop.wave.voting_credit_type;
  const votingCreditLabels = WAVE_VOTING_LABELS as Partial<Record<typeof votingCreditType, string>>;
  const votingCreditLabel = votingCreditLabels[votingCreditType] ?? votingCreditType;
  const t2 = `wave-leaderboard-grid-item-footer-${drop.id}`;
  let t3;
  if ($[2] !== drop.title) {
    t3 = drop.title && <h3 className="tw-mb-0 tw-truncate tw-text-sm tw-font-bold tw-leading-tight tw-text-iron-200">{drop.title}</h3>;
    $[2] = drop.title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== authorHandle) {
    t4 = authorHandle && <UserProfileTooltipWrapper user={authorHandle}><Link onClick={_temp} href={`/${authorHandle}`} className="tw-text-xs tw-text-iron-400 tw-no-underline tw-transition-colors tw-duration-150 desktop-hover:hover:tw-text-iron-300 desktop-hover:hover:tw-underline">{authorHandle}</Link></UserProfileTooltipWrapper>;
    $[4] = authorHandle;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <div className="tw-mr-2 tw-min-w-0 tw-flex-1">{t4}</div>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== drop || $[9] !== winningThreshold) {
    t6 = <GridItemRankBadge drop={drop} winningThreshold={winningThreshold} />;
    $[8] = drop;
    $[9] = winningThreshold;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="tw-flex tw-items-center tw-justify-between">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t7) {
    t8 = <div className="tw-mb-1.5">{t3}{t7}</div>;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== drop) {
    t9 = <WaveLeaderboardIdentity drop={drop} variant="condensed" className="tw-mb-3" supplementFullWidth={true} />;
    $[17] = drop;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== drop || $[20] !== isVotingClosed || $[21] !== winningThreshold || $[22] !== winningThresholdMinDurationMs) {
    t10 = <WaveLeaderboardGalleryItemVotes drop={drop} variant="subtle" winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} />;
    $[19] = drop;
    $[20] = isVotingClosed;
    $[21] = winningThreshold;
    $[22] = winningThresholdMinDurationMs;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="tw-size-3 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" /></svg>;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== drop.raters_count) {
    t12 = formatNumberWithCommas(drop.raters_count);
    $[25] = drop.raters_count;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <div className="tw-ml-4 tw-flex tw-items-center tw-gap-1 tw-text-iron-500">{t11}<span className="tw-font-medium">{t12}</span></div>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13) {
    t14 = <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-text-xs">{t10}{t13}</div>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== hasUserVoted || $[33] !== isNegativeVote || $[34] !== userVote || $[35] !== voteStyle || $[36] !== votingCreditLabel) {
    t15 = hasUserVoted && <span className="tw-font-mono tw-text-[11px] tw-text-iron-500">You:{" "}<span className={voteStyle}>{isNegativeVote && "-"}{formatNumberWithCommas(Math.abs(userVote))} {votingCreditLabel}</span></span>;
    $[32] = hasUserVoted;
    $[33] = isNegativeVote;
    $[34] = userVote;
    $[35] = voteStyle;
    $[36] = votingCreditLabel;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const t16 = drop.context_profile_context?.curatable ?? false;
  const t17 = drop.context_profile_context?.curated ?? false;
  let t18;
  if ($[38] !== drop.id || $[39] !== drop.wave.id || $[40] !== t16 || $[41] !== t17) {
    t18 = <DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={t16} isCurated={t17} />;
    $[38] = drop.id;
    $[39] = drop.wave.id;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== canShowVotingAction || $[44] !== drop || $[45] !== onVoteButtonClick) {
    t19 = canShowVotingAction && <VotingModalButton drop={drop} onClick={onVoteButtonClick} variant="subtle" />;
    $[43] = canShowVotingAction;
    $[44] = drop;
    $[45] = onVoteButtonClick;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t18 || $[48] !== t19) {
    t20 = <div className="tw-ml-auto tw-flex tw-items-center tw-gap-1.5">{t18}{t19}</div>;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t15 || $[51] !== t20) {
    t21 = <div className="tw-flex tw-items-center tw-justify-between tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800/50 tw-pt-2">{t15}{t20}</div>;
    $[50] = t15;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t14 || $[54] !== t2 || $[55] !== t21 || $[56] !== t8 || $[57] !== t9) {
    t22 = <div data-testid={t2} className="tw-rounded-b-lg tw-bg-iron-950/50 tw-px-3 tw-pb-3 tw-pt-3">{t8}{t9}{t14}{t21}</div>;
    $[53] = t14;
    $[54] = t2;
    $[55] = t21;
    $[56] = t8;
    $[57] = t9;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  return t22;
};
function _temp(e) {
  return e.stopPropagation();
}